import { getItem, type Quest, spellTaughtBy } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { describeOnHover } from './item-hover.js';
import { paintItemIcon } from './item-icon.js';
import { element, kitIcon } from './kit.js';
import { uiFormat, uiText } from './text.js';

/** What the giver hands over with the quest (a bait, ADR 0047), under the same slots as the reward. */
export function questGrants(quest: Quest, icons: CssAtlas | null): HTMLElement | undefined {
  if (quest.grants.length === 0) return undefined;
  const box = element('div', 'quest-rewards');
  box.append(element('span', 'quest-rewards__label', 'Remis avec la quête'));
  const row = element('div', 'quest-rewards__row');
  for (const grant of quest.grants) row.append(itemSlot(grant, icons));
  box.append(row);
  return box;
}

/** One item of a reward: its icon in a slot, its tooltip, its count. */
export function itemSlot(
  reward: { templateId: string; quantity: number },
  icons: CssAtlas | null,
): HTMLElement {
  const template = getItem(reward.templateId);
  const slot = element('span', 'quest-rewards__item');
  // The slot's frame fills its middle: the icon lies on a box of its own inside.
  const frame = element('span', 'quest-rewards__slot');
  const icon = element('span', 'quest-rewards__icon');
  paintItemIcon(icon, template, icons, reward.templateId);
  describeOnHover(frame, template);
  frame.append(icon);
  slot.append(frame);
  if (reward.quantity > 1) {
    slot.append(element('span', 'quest-rewards__count', `×${String(reward.quantity)}`));
  }
  return slot;
}

/** What a quest pays (ADR 0047), drawn with the real icons: experience, gold, items with their tooltip. */
export function questRewards(quest: Quest, icons: CssAtlas | null): HTMLElement {
  const box = element('div', 'quest-rewards');
  box.append(element('span', 'quest-rewards__label', 'Récompense'));
  const row = element('div', 'quest-rewards__row');
  if (quest.reward.xp > 0) {
    row.append(element('span', 'quest-rewards__xp', uiFormat('{xp} XP', { xp: quest.reward.xp })));
  }
  if (quest.reward.gold > 0) {
    const gold = element('span', 'quest-rewards__gold', String(quest.reward.gold));
    gold.prepend(kitIcon('gold'));
    row.append(gold);
  }
  for (const reward of quest.reward.items) row.append(itemSlot(reward, icons));
  box.append(row);
  // A region's signature spell comes with its quest (ADR 0048): the grimoire learns it on turn-in.
  const spell = spellTaughtBy(quest.id);
  if (spell !== undefined) {
    const taught = element('div', 'quest-rewards__spell');
    const icon = element('span', 'quest-rewards__spell-icon');
    icon.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
    taught.append(
      icon,
      element(
        'span',
        'quest-rewards__spell-name',
        uiFormat('Enseigne le sort : {spell}', { spell: spell.name }),
      ),
    );
    taught.title = uiText(spell.description);
    box.append(taught);
  }
  return box;
}
